import { format, parseISO } from 'date-fns';
import differenceInWeeks from 'date-fns/differenceInWeeks';

import type { Cents, FormattedDollars } from '../types/Common';

export const formatToSimpleDateString = (
    dateString: string | null | undefined,
    options?: Intl.DateTimeFormatOptions,
): string => {
    if (dateString == null) return '';
    const inputDate = parseISO(dateString);
    if (isNaN(inputDate.getDate())) return 'Unknown'; // Invalid date

    return inputDate.toLocaleDateString(undefined, options);
};

export const formatToSimpleDateTimeString = (
    dateString: string | Date | null | undefined,
    options?: Intl.DateTimeFormatOptions,
): string => {
    if (dateString == null) return '';
    const inputDate =
        typeof dateString === 'string' ? parseISO(dateString) : dateString;
    if (isNaN(inputDate.getDate())) return 'Unknown'; // Invalid date

    const defaultOptions: Intl.DateTimeFormatOptions = {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    };

    const formatOptions = {
        ...defaultOptions,
        ...options,
    };

    return inputDate.toLocaleString(undefined, formatOptions);
};

export const formatTenure = (
    dateString: string | null | undefined,
): { value: string; isNew: boolean } => {
    if (dateString == null) return { value: '', isNew: false };

    const currentDate = new Date();
    const inputDate = parseISO(dateString);
    if (isNaN(inputDate.getDate())) return { value: 'Unknown', isNew: false }; // Invalid date

    let years = currentDate.getFullYear() - inputDate.getFullYear();
    let months = currentDate.getMonth() - inputDate.getMonth();

    if (months < 0) {
        years--;
        months += 12;
    }

    if (years === 0 && months < 3) {
        const weeks = differenceInWeeks(currentDate, inputDate);
        return { value: `${weeks} wk`, isNew: true };
    } else if (years === 0) {
        return { value: ` ${months} mo`, isNew: false };
    } else if (months === 0) {
        return { value: `${years} yr`, isNew: false };
    } else {
        return { value: `${years} yr ${months} mo`, isNew: false };
    }
};

export const roundToTwoDecimals = (value: number): number => {
    return Math.round(value * 100) / 100;
};

export const centsToDollars = (cents: number): number => {
    return roundToTwoDecimals(cents / 100);
};

export const formatDollars = (
    dollarValue: number = 0,
    showDecimals = true,
): string => {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        ...(showDecimals
            ? {}
            : { maximumFractionDigits: 0, minimumFractionDigits: 0 }),
    });
    return formatter.format(dollarValue);
};

export const centsToFormattedDollars = (
    value: Cents,
    showDecimals = true,
): FormattedDollars => {
    return formatDollars(centsToDollars(value), showDecimals);
};

export const formatPhoneNumber = (phoneNumber?: string | null): string => {
    if (phoneNumber == null) {
        return '';
    }

    // Remove non-numeric characters from the input string
    const numericString = phoneNumber.replace(/\D/g, '');

    // Use regular expressions to format the numeric string
    return numericString.replace(
        /^(\d{1})(\d{3})(\d{3})(\d{4})$/,
        '+$1 ($2) $3-$4',
    );
};

export const formatToFullPhoneNumber = (phoneNumber: string) => {
    if (phoneNumber.startsWith('+1') && phoneNumber.length === 12) {
        return phoneNumber;
    } else if (phoneNumber.length === 10) {
        return `+1${phoneNumber}`;
    } else if (phoneNumber.startsWith('1') && phoneNumber.length === 11) {
        return `+${phoneNumber}`;
    }

    throw new Error('Phone number not formatted correctly.');
};

/**
 * Format a person/guide display name from firstName + lastName, with optional
 * fallback to email (or a custom fallback string).
 */
export const getPersonDisplayName = (
    person:
        | {
              firstName?: string | null;
              lastName?: string | null;
              email?: string | null;
          }
        | null
        | undefined,
    fallback = '—',
): string => {
    if (person == null) return fallback;
    const first = person.firstName?.trim() ?? '';
    const last = person.lastName?.trim() ?? '';
    const fullName = `${first} ${last}`.trim();
    if (fullName !== '') {
        return fullName;
    }
    const em = person.email?.trim();
    if (em != null && em !== '') {
        return em;
    }
    return fallback;
};

export const getMonthString = (monthNumber: string): string | null => {
    const monthIndex = parseInt(monthNumber, 10) - 1;

    if (monthIndex < 0 || monthIndex > 11 || isNaN(monthIndex)) {
        return null;
    }

    const date = new Date(2000, monthIndex);
    return format(date, 'MMMM');
};
